import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import U2000InputTextBase from "../../../formControls/input/U2000InputTextBase";
import {
  computeIntervalEndFromStartOffset,
  computeIntervalStartFromEndOffset,
  type DateCriteriaRange,
  filterDigitsOnly,
  INTERVAL_DAY_OFFSET_PRESETS,
  parsePositiveDays,
} from "./u2000DateCriteriaUtils";

export interface U2000SearchCriteriaIntervalPresetsPanelProps {
  range: DateCriteriaRange;
  onRangeChange: (range: DateCriteriaRange) => void;
  disabled?: boolean;
}

type CustomDaysDirection = "endFromStart" | "startFromEnd";

interface PresetColumnProps {
  title: string;
  anchorYmd?: string;
  direction: CustomDaysDirection;
  disabled: boolean;
  customDays: string;
  onCustomDaysChange: (direction: CustomDaysDirection, value: string) => void;
  onApplyOffset: (offsetDays: number) => void;
}

function PresetColumn({
  title,
  anchorYmd,
  direction,
  disabled,
  customDays,
  onCustomDaysChange,
  onApplyOffset,
}: PresetColumnProps) {
  const { t } = useTranslation("common");
  const anchorMissing = !anchorYmd;
  const columnDisabled = disabled || anchorMissing;

  const presetLabelKey =
    direction === "endFromStart"
      ? "lblSearchCriteriaIntervalPresetNextDays"
      : "lblSearchCriteriaIntervalPresetPreviousDays";

  function applyCustomOffset(): void {
    const n = parsePositiveDays(customDays);
    if (n == null) {
      return;
    }
    onApplyOffset(n);
  }

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col gap-2">
      <span className="text-xs font-semibold text-gray-700">{title}</span>
      {anchorMissing && (
        <p className="text-xs text-gray-500">
          {direction === "endFromStart"
            ? t("lblSearchCriteriaIntervalPresetNeedStart")
            : t("lblSearchCriteriaIntervalPresetNeedEnd")}
        </p>
      )}
      <div className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto">
        {INTERVAL_DAY_OFFSET_PRESETS.map((days) => (
          <button
            key={days}
            type="button"
            disabled={columnDisabled}
            onClick={() => onApplyOffset(days)}
            className="rounded-lg border border-gray-200 bg-white px-2 py-1.5 text-left text-xs font-medium text-gray-800 hover:border-blue-300 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t(presetLabelKey, { count: days })}
          </button>
        ))}
      </div>
      <div className="mt-1 flex flex-col gap-1.5 border-t border-gray-200 pt-2">
        <span className="text-xs font-medium text-gray-500">
          {direction === "endFromStart"
            ? t("lblSearchCriteriaIntervalPresetCustomDays")
            : t("lblSearchCriteriaIntervalPresetCustomDaysBackward")}
        </span>
        <div className="flex gap-1.5">
          <div className="min-w-0 flex-1">
            <U2000InputTextBase
              fieldName={`criteriaIntervalPresetCustom-${direction}`}
              type="string"
              disabled={columnDisabled}
              value={customDays}
              setValue={(v) => onCustomDaysChange(direction, String(v))}
              placeholder="N"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyCustomOffset();
                }
              }}
            />
          </div>
          <button
            type="button"
            disabled={columnDisabled || parsePositiveDays(customDays) == null}
            onClick={applyCustomOffset}
            className="shrink-0 rounded-lg border border-blue-600 bg-blue-600 px-2 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {t("lblSearchCriteriaIntervalPresetApply")}
          </button>
        </div>
      </div>
    </div>
  );
}

const U2000SearchCriteriaIntervalPresetsPanel: React.FC<U2000SearchCriteriaIntervalPresetsPanelProps> = ({
  range,
  onRangeChange,
  disabled = false,
}) => {
  const { t } = useTranslation("common");
  const [customDaysEndFromStart, setCustomDaysEndFromStart] = useState("");
  const [customDaysStartFromEnd, setCustomDaysStartFromEnd] = useState("");

  const handleCustomDaysChange = useCallback((direction: CustomDaysDirection, raw: string) => {
    const digits = filterDigitsOnly(raw);
    if (direction === "endFromStart") {
      setCustomDaysEndFromStart(digits);
      setCustomDaysStartFromEnd("");
    } else {
      setCustomDaysStartFromEnd(digits);
      setCustomDaysEndFromStart("");
    }
  }, []);

  function applyEndFromStart(offsetDays: number): void {
    if (!range.start) {
      return;
    }
    const next = computeIntervalEndFromStartOffset(range.start, offsetDays);
    if (next) {
      onRangeChange(next);
    }
  }

  function applyStartFromEnd(offsetDays: number): void {
    if (!range.end) {
      return;
    }
    const next = computeIntervalStartFromEndOffset(range.end, offsetDays);
    if (next) {
      onRangeChange(next);
    }
  }

  return (
    <aside
      className="flex w-full shrink-0 flex-col self-stretch rounded-lg border border-gray-200 bg-gray-50 p-3 xl:w-88"
      aria-label={t("lblSearchCriteriaIntervalPresetsPanel")}
    >
      <div className="grid h-full min-h-0 flex-1 grid-cols-1 gap-4 sm:grid-cols-2 sm:items-stretch">
        <PresetColumn
          title={t("lblSearchCriteriaIntervalPresetsEndFromStart")}
          anchorYmd={range.start}
          direction="endFromStart"
          disabled={disabled}
          customDays={customDaysEndFromStart}
          onCustomDaysChange={handleCustomDaysChange}
          onApplyOffset={applyEndFromStart}
        />
        <PresetColumn
          title={t("lblSearchCriteriaIntervalPresetsStartFromEnd")}
          anchorYmd={range.end}
          direction="startFromEnd"
          disabled={disabled}
          customDays={customDaysStartFromEnd}
          onCustomDaysChange={handleCustomDaysChange}
          onApplyOffset={applyStartFromEnd}
        />
      </div>
    </aside>
  );
};

export default U2000SearchCriteriaIntervalPresetsPanel;
